import { inject, Injectable } from '@angular/core';
import { LoggableAPI } from '../models/protocols/loggable-api';
import { ApiClient } from './api-client';
import { Endpoints } from './endpoints';
import { CreateMemberRequest } from '../models/users/requests/create-member-request';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CustomError } from '../models/shared/custom-error';
import { MemberUser } from '../models/users/dto/member-user';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { ChangeEmailRequest } from '../models/users/requests/change-email-request';
import { HttpResponse } from '@angular/common/http';
import { ODataResponse } from '../models/protocols/odata-response';
import { MemberUserSearchResult } from '../models/users/dto/member-user-search-result';
import { MemberRecordCommentRequest } from '../models/users/requests/member-record-comment-request';
import { MemberRecordComment } from '../views/records/components/members/ip-view-member-record/components/ip-member-record-comments/ip-member-record-comment';
import { Mentionable } from '../models/base/mentionable';
import { EmploymentHistory } from '../models/users/dto/employment-history';
import { ResendMemberInviteRequest } from '../models/users/requests/resend-member-invite-request';
import { MostRecentInvite } from '../models/users/dto/most-recent-invite';
import { MemberRecord } from '../models/users/dto/member-record';
import { MemberPaymentMethod } from '../models/users/dto/member-payment-method';
import { ThirdPartyContributionConsentUpdateRequest } from '../models/users/requests/third-party-contribution-consent-update-request';
import { IpThirdPartyContributionConsentUpdateRequest } from '../models/users/requests/ip-third-party-contribution-consent-update-request';
import { MemberCreditCard } from '../models/contributions/member-credit-card';
import { MemberContribution } from '../models/contributions/member-contribution';
import { MemberScheduledContribution } from '../models/contributions/member-scheduled-contribution';
import { MpSpousalContributionYearToDate } from '../models/transactions/mp-spousal-contribution-year-to-date';
import { PaymentMethodUsage } from '../models/contributions/payment-method-usage';
import { CreditCardStatusUpdateRequest } from '../models/contributions/requests/credit-card-status-update-request';
import {
  CreditCardCreateRequest,
  IpCreditCardCreateRequest
} from '../models/contributions/requests/credit-card-create-request';
import {
  IpOneTimeContributionCreateRequest,
  OneTimeContributionCreateRequest
} from '../models/contributions/requests/one-time-contribution-create-request';
import {
  IpScheduledContributionCreateRequest,
  ScheduledContributionCreateRequest
} from '../models/contributions/requests/scheduled-contribution-create-request';
import { IpMemberSpouseContactInfoRequest } from '../models/users/requests/ip-member-spouse-contact-info-request';
import { MemberRelationshipStatus } from '../models/users/dto/member-relationship-status';
import { MpSpouseContactInfoRequest } from '../models/users/requests/mp-spouse-contact-info-request';
import { MemberOnboardingProgress } from '../models/members/member-onboarding-progress';
import { MemberPersonalInformationOnboardingRequest } from '../models/users/requests/member-personal-information-onboarding-request';
import { MpSelfEnrolmentRequest } from '../models/users/requests/mp-self-enrolment-request';
import { SubmissionAttachment } from '../models/submissions/submission-attachment';
import { NewMemberVerificationRequest } from '../models/members/new-member-verification-request';
import { NewMemberVerificationResponse } from '../models/members/new-member-verification-response';
import { ActiveRemittance } from '../models/remittances/active-remittance';
import { EmployeeUser } from '../models/users/dto/employee-user';
import { MemberRetirementStub } from '../models/retirement-stub/member-retirement-stub';
import { MemberRetirementType } from '../models/retirement-stub/member-retirement-type';
import { MemberRetirementStubUpdateRequest } from '../models/retirement-stub/member-retirement-stub-update-request';
import { MemberLifeStatus } from '../models/life-status/member-life-status';
import { MemberLifeStatusUpdateRequest } from '../models/life-status/member-life-status-update-request';
import { MemberPaymentSuspension } from '../models/members/member-payment-suspension';

@Injectable({
  providedIn: 'root'
})
export class MembersAPI implements LoggableAPI {
  private apiClient = inject(ApiClient);

  constructor() {}

  public serviceName = 'Members';

  public createEmployerMember(req: CreateMemberRequest): Observable<MemberUser> {
    const url = Endpoints.createEmployee();
    return this.apiClient.postObj(MemberUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateMember(req: CreateMemberRequest): Observable<MemberUser> {
    const url = Endpoints.ipCreateMember();
    return this.apiClient.postObj(MemberUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateNonMember(req: CreateMemberRequest): Observable<MemberUser> {
    const url = Endpoints.ipCreateNonMember();
    return this.apiClient.postObj(MemberUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateMemberOnboardingInformation(req: MemberPersonalInformationOnboardingRequest): Observable<MemberUser> {
    const url = Endpoints.updateMemberOnboardingInformation();
    return this.apiClient.putObj<MemberUser>(MemberUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberCommentMentionables(opts: ODataQueryOptions): Observable<ODataResponse<Mentionable>> {
    const url = Endpoints.ipGetMemberCommentMentionables();
    return this.apiClient.getOdata<Mentionable>(url, Mentionable, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberOnboardingProgress(opts: ODataQueryOptions): Observable<MemberOnboardingProgress> {
    const url = Endpoints.getMemberOnboardingProgress();
    return this.apiClient.getOdataObj<MemberOnboardingProgress>(url, MemberOnboardingProgress, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetActiveRemittancesForMember(memberId: number): Observable<ActiveRemittance[]> {
    const url = Endpoints.ipGetActiveRemittancesForMember(memberId);
    return this.apiClient.getArr<ActiveRemittance>(ActiveRemittance, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberById(memberId: number, odataQueryOptions: ODataQueryOptions): Observable<MemberRecord> {
    const url = Endpoints.ipGetMemberById(memberId);
    return this.apiClient.getOdataObj<MemberRecord>(url, MemberRecord, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberRetirementStub(memberId: number): Observable<MemberRetirementStub | null> {
    const url = Endpoints.ipGetMemberRetirementStub(memberId);
    return this.apiClient.getObj<MemberRetirementStub>(MemberRetirementStub, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberRetirementStub(request: MemberRetirementStubUpdateRequest): Observable<MemberRetirementStub> {
    const url = Endpoints.ipUpdateMemberRetirementStub(request.memberId, request.id);
    return this.apiClient.putObj<MemberRetirementStub>(MemberRetirementStub, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberLifeStatus(memberId: number): Observable<MemberLifeStatus> {
    const url = Endpoints.ipGetMemberLifeStatus(memberId);
    return this.apiClient.getObj<MemberLifeStatus>(MemberLifeStatus, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberLifeStatus(request: MemberLifeStatusUpdateRequest): Observable<MemberLifeStatus> {
    const url = Endpoints.ipUpdateMemberLifeStatus(request.memberId);
    return this.apiClient.putObj<MemberLifeStatus>(MemberLifeStatus, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberPaymentSuspension(memberId: number): Observable<MemberPaymentSuspension> {
    const url = Endpoints.ipGetMemberPaymentSuspension(memberId);
    return this.apiClient.getObj<MemberPaymentSuspension>(MemberPaymentSuspension, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSuspendMemberPayments(memberId: number): Observable<MemberPaymentSuspension> {
    const url = Endpoints.ipSuspendMemberPayments(memberId);
    return this.apiClient.putObj<MemberPaymentSuspension>(MemberPaymentSuspension, url, { memberId }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipResumeMemberPayments(memberId: number): Observable<MemberPaymentSuspension> {
    const url = Endpoints.ipResumeMemberPayments(memberId);
    return this.apiClient.putObj<MemberPaymentSuspension>(MemberPaymentSuspension, url, { memberId }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipRestrictMemberPortalAccess(memberId: number): Observable<MemberRecord> {
    const url = Endpoints.ipRestrictMemberPortalAccess(memberId);
    return this.apiClient.putObj<MemberRecord>(MemberRecord, url, { memberId }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipRestoreMemberPortalAccess(memberId: number): Observable<MemberRecord> {
    const url = Endpoints.ipRestoreMemberPortalAccess(memberId);
    return this.apiClient.putObj<MemberRecord>(MemberRecord, url, { memberId }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateThirdPartyContributionConsent(
    request: IpThirdPartyContributionConsentUpdateRequest
  ): Observable<MemberRecord> {
    const url = Endpoints.ipUpdateThirdPartyContributionConsent();
    return this.apiClient.putObj<MemberRecord>(MemberRecord, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getEmployerMemberById(memberId: number, odataQueryOptions: ODataQueryOptions): Observable<MemberUser> {
    const url = Endpoints.getEmployerMemberById(memberId);
    return this.apiClient.getOdataObj<MemberUser>(url, MemberUser, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public forceChangeEmail(u: MemberUser, req: ChangeEmailRequest): Observable<MemberUser> {
    const url = Endpoints.memberForceChangeEmail(u?.id.toString(10));
    return this.apiClient.postObj(MemberUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public forceChangePassword(memberId: string): Observable<HttpResponse<any>> {
    const url = Endpoints.memberForceChangePassword(memberId);
    return this.apiClient.simpleBodylessPost(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSearchMembersByIdOrName(
    odataQueryOptions: ODataQueryOptions,
    employerId?: number
  ): Observable<ODataResponse<MemberUserSearchResult>> {
    const url = Endpoints.searchInternalMembersByIdOrName();
    const additionalParams = employerId !== undefined ? 'employerId=' + employerId?.toString() : '';
    return this.apiClient
      .getOdata<MemberUserSearchResult>(url, MemberUserSearchResult, odataQueryOptions, null, additionalParams)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public epSearchMembersByIdOrName(
    odataQueryOptions: ODataQueryOptions,
    employerId?: number
  ): Observable<ODataResponse<MemberUserSearchResult>> {
    const url = Endpoints.searchEmployerMembersByIdOrName();
    const additionalParams = employerId !== undefined ? 'employerId=' + employerId?.toString() : '';
    return this.apiClient
      .getOdata<MemberUserSearchResult>(url, MemberUserSearchResult, odataQueryOptions, null, additionalParams)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public getAllInternalMembers(odataQueryOptions: ODataQueryOptions): Observable<ODataResponse<MemberUser>> {
    const url = Endpoints.getAllInternalMembers();
    return this.apiClient.getOdata<MemberUser>(url, MemberUser, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployerMembers(odataQueryOptions: ODataQueryOptions): Observable<ODataResponse<EmployeeUser>> {
    const url = Endpoints.epGetEmployerMembers();
    return this.apiClient.getOdata<EmployeeUser>(url, EmployeeUser, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getAllMembers(odataQueryOptions: ODataQueryOptions): Observable<ODataResponse<MemberUser>> {
    const url = Endpoints.getAllMembers();
    return this.apiClient.getOdata<MemberUser>(url, MemberUser, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetMember(odataQueryOptions: ODataQueryOptions): Observable<MemberUser> {
    const url = Endpoints.mpGetMember();
    return this.apiClient.getOdataObj<MemberUser>(url, MemberUser, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpUpdateThirdPartyContributionConsent(
    request: ThirdPartyContributionConsentUpdateRequest
  ): Observable<MemberUser> {
    const url = Endpoints.mpUpdateThirdPartyContributionConsent();
    return this.apiClient.putObj<MemberUser>(MemberUser, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetMemberRetirementType(): Observable<MemberRetirementType> {
    const url = Endpoints.mpGetMemberRetirementType();
    return this.apiClient.getObj<MemberRetirementType>(MemberRetirementType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public addCommentToMemberRecord(req: MemberRecordCommentRequest): Observable<MemberRecordComment[]> {
    const url = Endpoints.memberComments(req.memberId);
    return this.apiClient.simplePost(url, req).pipe(
      map(res => {
        const comments = window?.injector?.Deserialize?.arrayOf(MemberRecordComment, res.body);
        return comments.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
      }),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateInternalMemberEmploymentHistory(req: EmploymentHistory): Observable<EmploymentHistory> {
    const url = Endpoints.updateInternalEmploymentHistory(req.memberId.toString(), req.id.toString());
    return this.apiClient.putObj<EmploymentHistory>(EmploymentHistory, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateMemberEmploymentHistory(req: EmploymentHistory): Observable<EmploymentHistory> {
    const url = Endpoints.updateEmploymentHistory(req.memberId.toString(), req.id.toString());
    return this.apiClient.putObj<EmploymentHistory>(EmploymentHistory, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createEmployerHistory(req: EmploymentHistory, memberId: string): Observable<EmploymentHistory> {
    const url = Endpoints.createEmploymentHistory(memberId);
    return this.apiClient.postObj(EmploymentHistory, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public resendMemberInvite(memberId: string, req: ResendMemberInviteRequest): Observable<MostRecentInvite> {
    const url = Endpoints.resendMemberInvite(memberId);
    return this.apiClient.postObj(MostRecentInvite, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpUpdateMemberSpouseContactInfo(req: MpSpouseContactInfoRequest): Observable<MemberRelationshipStatus> {
    const url = Endpoints.mpUpdateSpouseContactInfo(req.id);
    return this.apiClient.putObj(MemberRelationshipStatus, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public IpUpdateMemberSpouseContactInfo(req: IpMemberSpouseContactInfoRequest): Observable<MemberRelationshipStatus> {
    const url = Endpoints.ipUpdateMemberSpouseContactInfo(req.memberId, req.spouseId);
    return this.apiClient.putObj(MemberRelationshipStatus, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpSelfEnrollMember(req: MpSelfEnrolmentRequest): Observable<SubmissionAttachment[]> {
    const url = Endpoints.mpSelfEnroll();
    return this.apiClient.simplePost(url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      }),
      map(res => {
        return res?.body as SubmissionAttachment[];
      })
    );
  }

  public ipGetNewMembersToVerify(req: NewMemberVerificationRequest[]): Observable<NewMemberVerificationResponse[]> {
    const url = Endpoints.ipGetNewMembersToVerify();
    return this.apiClient.postArr(NewMemberVerificationResponse, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateNewMembers(req: CreateMemberRequest[]): Observable<MemberUser[]> {
    const url = Endpoints.ipCreateMembers();
    return this.apiClient.postArr(MemberUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epCreateEmployees(req: CreateMemberRequest[]): Observable<HttpResponse<any>> {
    const url = Endpoints.epCreateEmployees();
    return this.apiClient.postWithUntypedRes(url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberPaymentMethods(memberId: number): Observable<MemberPaymentMethod[]> {
    const url = Endpoints.ipGetMemberPaymentMethods(memberId);
    return this.apiClient.getArr<MemberPaymentMethod>(MemberPaymentMethod, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetMemberPaymentMethods(): Observable<MemberPaymentMethod[]> {
    const url = Endpoints.mpGetMemberPaymentMethods();
    return this.apiClient.getArr<MemberPaymentMethod>(MemberPaymentMethod, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateMemberCreditCard(memberId: number, request: IpCreditCardCreateRequest): Observable<MemberCreditCard> {
    const url = Endpoints.ipCreateMemberCreditCard(memberId);
    return this.apiClient.postObj(MemberCreditCard, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberCreditCards(memberId: number): Observable<MemberCreditCard[]> {
    const url = Endpoints.ipGetMemberCreditCards(memberId);
    return this.apiClient.getArr<MemberCreditCard>(MemberCreditCard, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberCreditCardStatus(
    memberId: number,
    request: CreditCardStatusUpdateRequest
  ): Observable<HttpResponse<any>> {
    const url = Endpoints.ipUpdateMemberCreditCardStatus(memberId, request.id);
    return this.apiClient.putWithUntypedRes(url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberCreditCardUsage(memberId: number, creditCardId: number): Observable<PaymentMethodUsage> {
    const url = Endpoints.ipGetMemberCreditCardUsage(memberId, creditCardId);
    return this.apiClient.getObj<PaymentMethodUsage>(PaymentMethodUsage, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateMemberContribution(
    memberId: number,
    request: IpOneTimeContributionCreateRequest
  ): Observable<MemberContribution> {
    const url = Endpoints.ipCreateMemberContribution(memberId);
    return this.apiClient.postObj(MemberContribution, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateMemberScheduledContribution(
    memberId: number,
    request: IpScheduledContributionCreateRequest
  ): Observable<MemberScheduledContribution> {
    const url = Endpoints.ipCreateMemberScheduledContribution(memberId);
    return this.apiClient.postObj(MemberScheduledContribution, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberScheduledContributions(memberId: number): Observable<MemberScheduledContribution[]> {
    const url = Endpoints.ipGetMemberScheduledContributions(memberId);
    return this.apiClient.getArr<MemberScheduledContribution>(MemberScheduledContribution, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetMemberScheduledContributions(): Observable<MemberScheduledContribution[]> {
    const url = Endpoints.mpGetMemberScheduledContributions();
    return this.apiClient.getArr<MemberScheduledContribution>(MemberScheduledContribution, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetSpousalContributionsYearToDate(): Observable<MpSpousalContributionYearToDate[]> {
    const url = Endpoints.mpGetSpousalContributionsYearToDate();
    return this.apiClient.getArr<MpSpousalContributionYearToDate>(MpSpousalContributionYearToDate, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCancelMemberScheduledContribution(
    memberId: number,
    scheduledContributionId: number
  ): Observable<MemberScheduledContribution> {
    const url = Endpoints.ipCancelMemberScheduledContribution(memberId, scheduledContributionId);
    return this.apiClient.putObj<MemberScheduledContribution>(MemberScheduledContribution, url, null).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetNextFiveScheduledContributionDates(
    memberId: number,
    frequencyId: number,
    startDate: string,
    endDate: string | null = null
  ): Observable<string[]> {
    const url = Endpoints.ipGetNextFiveScheduledContributionDates(memberId, frequencyId, startDate, endDate);
    return this.apiClient.getPrimitiveArr<string>(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCreateMemberCreditCard(request: CreditCardCreateRequest): Observable<MemberCreditCard> {
    const url = Endpoints.mpCreateMemberCreditCard();
    return this.apiClient.postObj(MemberCreditCard, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetMemberCreditCards(): Observable<MemberCreditCard[]> {
    const url = Endpoints.mpGetMemberCreditCards();
    return this.apiClient.getArr<MemberCreditCard>(MemberCreditCard, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpUpdateMemberCreditCardStatus(request: CreditCardStatusUpdateRequest): Observable<HttpResponse<any>> {
    const url = Endpoints.mpUpdateMemberCreditCardStatus(request.id);
    return this.apiClient.putWithUntypedRes(url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetMemberCreditCardUsage(creditCardId: number): Observable<PaymentMethodUsage> {
    const url = Endpoints.mpGetMemberCreditCardUsage(creditCardId);
    return this.apiClient.getObj<PaymentMethodUsage>(PaymentMethodUsage, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCreateMemberContribution(request: OneTimeContributionCreateRequest): Observable<MemberContribution> {
    const url = Endpoints.mpCreateMemberContribution();
    return this.apiClient.postObj(MemberContribution, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCreateMemberScheduledContribution(
    request: ScheduledContributionCreateRequest
  ): Observable<MemberScheduledContribution> {
    const url = Endpoints.mpCreateMemberScheduledContribution();
    return this.apiClient.postObj(MemberScheduledContribution, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCancelMemberScheduledContribution(scheduledContributionId: number): Observable<MemberScheduledContribution> {
    const url = Endpoints.mpCancelMemberScheduledContribution(scheduledContributionId);
    return this.apiClient.putObj<MemberScheduledContribution>(MemberScheduledContribution, url, null).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetNextFiveScheduledContributionDates(
    frequencyId: number,
    startDate: string,
    endDate: string | null = null
  ): Observable<string[]> {
    const url = Endpoints.mpGetNextFiveScheduledContributionDates(frequencyId, startDate, endDate);
    return this.apiClient.getPrimitiveArr<string>(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }
}
